/**
 * Dashboard Page
 * @implements US-099 - Backoffice Dashboard View
 *
 * Displays three sections for a project:
 * - Pending Validations: tickets awaiting validator feedback
 * - Pending Consensus: tickets with all feedback, awaiting consensus
 * - PO Approval Queue: tickets awaiting PO validation
 *
 * Read-only view with manual refresh. Pull-based (no subscriptions).
 */

import { gql, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import type { DashboardItem } from '@/types';

/**
 * GraphQL query fragments and operations for dashboard data
 * @implements US-098 - Dashboard GraphQL Queries
 */
const DASHBOARD_ITEM_FRAGMENT = gql`
  fragment DashboardItemFields on DashboardItem {
    ticketId
    ticketName
    ticketType
    specReferences
    column
    validationFeedbackSummary {
      total
      approved
      issues
      pending
    }
  }
`;

const PENDING_VALIDATIONS_QUERY = gql`
  ${DASHBOARD_ITEM_FRAGMENT}
  query PendingValidations($projectId: ID!) {
    pendingValidations(projectId: $projectId) {
      ...DashboardItemFields
    }
  }
`;

const PENDING_CONSENSUS_QUERY = gql`
  ${DASHBOARD_ITEM_FRAGMENT}
  query PendingConsensus($projectId: ID!) {
    pendingConsensus(projectId: $projectId) {
      ...DashboardItemFields
    }
  }
`;

const PENDING_PO_APPROVAL_QUERY = gql`
  ${DASHBOARD_ITEM_FRAGMENT}
  query PendingPoApproval($projectId: ID!) {
    pendingPoApproval(projectId: $projectId) {
      ...DashboardItemFields
    }
  }
`;

interface QueryData {
  pendingValidations?: DashboardItem[];
  pendingConsensus?: DashboardItem[];
  pendingPoApproval?: DashboardItem[];
}

/**
 * Formats the validation feedback summary into a human-readable string.
 * @implements US-099 - Each card shows validation feedback summary
 */
function formatFeedbackSummary(item: DashboardItem): string {
  const { total, approved, issues, pending } =
    item.validationFeedbackSummary;
  const parts: string[] = [];
  if (approved > 0) parts.push(`${approved} approved`);
  if (issues > 0) parts.push(`${issues} issues`);
  if (pending > 0) parts.push(`${pending} pending`);
  if (parts.length === 0) return `${total} total`;
  return parts.join(', ');
}

/**
 * Returns a color for the ticket type chip.
 */
function ticketTypeColor(
  ticketType: string,
): 'primary' | 'secondary' | 'default' {
  switch (ticketType.toUpperCase()) {
    case 'MILESTONE':
      return 'primary';
    case 'EPIC':
      return 'secondary';
    default:
      return 'default';
  }
}

/**
 * Renders a single dashboard item card.
 * @implements US-099 - Each card shows: ticket name, ticket type, spec references
 */
const DashboardItemCard = ({ item }: { item: DashboardItem }) => (
  <Card variant="outlined" sx={{ mb: 1.5 }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {item.ticketName}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={item.ticketType}
              size="small"
              color={ticketTypeColor(item.ticketType)}
              variant="outlined"
            />
            {item.specReferences.map((ref) => (
              <Chip key={ref} label={ref} size="small" variant="outlined" />
            ))}
          </Stack>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
        >
          {formatFeedbackSummary(item)}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

/**
 * Renders a dashboard section with a title and list of items.
 * @implements US-099 - Empty sections show "No pending items" message
 */
const DashboardSection = ({
  title,
  items,
  loading,
  error,
  onRetry,
}: {
  title: string;
  items: DashboardItem[] | undefined;
  loading: boolean;
  error: Error | undefined;
  onRetry: () => void;
}) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ mb: 1.5 }}>
      {title}
      {items && items.length > 0 && (
        <Chip
          label={items.length}
          size="small"
          color="primary"
          sx={{ ml: 1, verticalAlign: 'middle' }}
        />
      )}
    </Typography>
    <Divider sx={{ mb: 2 }} />

    {loading && (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    )}

    {error && (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
        sx={{ mb: 1 }}
      >
        {error.message}
      </Alert>
    )}

    {!loading && !error && items?.length === 0 && (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No pending items
      </Typography>
    )}

    {items?.map((item) => (
      <DashboardItemCard key={item.ticketId} item={item} />
    ))}
  </Box>
);

/**
 * Dashboard page component.
 * Fetches and displays pending validations, consensus, and PO approval items.
 *
 * Route: /projects/:projectId/dashboard
 * @implements US-099 - Backoffice Dashboard View
 */
export const DashboardPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const validations = useQuery<QueryData>(PENDING_VALIDATIONS_QUERY, {
    variables: { projectId },
    skip: !projectId,
  });

  const consensus = useQuery<QueryData>(PENDING_CONSENSUS_QUERY, {
    variables: { projectId },
    skip: !projectId,
  });

  const poApproval = useQuery<QueryData>(PENDING_PO_APPROVAL_QUERY, {
    variables: { projectId },
    skip: !projectId,
  });

  const isAnyLoading =
    validations.loading || consensus.loading || poApproval.loading;

  const handleRefreshAll = () => {
    validations.refetch();
    consensus.refetch();
    poApproval.refetch();
  };

  if (!projectId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">No project ID provided.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Project Dashboard</Typography>
        </Box>
        <Tooltip title="Refresh all sections">
          <IconButton onClick={handleRefreshAll} disabled={isAnyLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <DashboardSection
        title="Pending Validations"
        items={validations.data?.pendingValidations}
        loading={validations.loading}
        error={validations.error}
        onRetry={() => validations.refetch()}
      />

      <DashboardSection
        title="Pending Consensus"
        items={consensus.data?.pendingConsensus}
        loading={consensus.loading}
        error={consensus.error}
        onRetry={() => consensus.refetch()}
      />

      <DashboardSection
        title="PO Approval Queue"
        items={poApproval.data?.pendingPoApproval}
        loading={poApproval.loading}
        error={poApproval.error}
        onRetry={() => poApproval.refetch()}
      />
    </Container>
  );
};
